import * as React from 'react';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import CheckIcon from '@mui/icons-material/Check';
import LensIcon from '@mui/icons-material/Lens';
import StarIcon from '@mui/icons-material/Star';
import Typography from '@mui/material/Typography';
import { TimelineOppositeContent } from '@mui/lab';
import { useEffect, useRef, useState } from 'react';
import { Box, Paper, Tooltip } from '@mui/material';

import { ObjectCardHorizontal } from '../ObjectCardHorizontal';
import { BaseComponent } from '../Component';
import theme from '../../theme/theme';
import { CardImageEnum, CardImageSize, CardSize } from '../ObjectCard';
import { URLIconEnum } from '../ObjectCardHorizontal';
import { AdditionalResource } from '../ObjectCardHorizontal/AdditionalResourceList';

import { TimelineIconEnum } from './TimelineIconEnums';

export interface VerticalTimelineComponent extends BaseComponent {
  type: 'verticaltimeline';
  props: VerticalTimelineProps;
}
export interface VerticalTimelineProps {
  items: VerticalTimelineItem[];
  /**
   * when set,timeline scrolls automatically to Item with id === scrollToId
   * scrollToId = -1 scrolls to top of timeline
   */
  scrollToId?: number;
  /**
   * used for recursion
   */
  mapKey?: string;
  /**
   * handle clicks on the dots
   */
  dotClickHandler?: (id: number) => void;
  displayTexts?: DisplayTextProps;
}
/**
 * VerticalTimelineItem is similar to ProgramModule in Creator
 */
export interface VerticalTimelineItem {
  /**
   * time line icon is defined by evoach and can be extended
   */
  timelineIcon?: TimelineIconEnum;
  timelineText?: string;
  title?: string;
  description?: string;
  duration?: string;
  image?: CardImageEnum;
  resolvedSrc?: string;
  imageSize?: CardImageSize;
  cardSize?: CardSize;
  /**
   * this corresponds to action.link in ProgramModuleAction definition in Creator
   */
  url?: string;
  /**
   * this corresponds to action.linkType in ProgramModuleAction definition in Creator.
   * These enums are not equal, you have to map the ProgramModuleActionLinkTypeEnum
   * to URLIconEnum.
   */
  urlIcon?: URLIconEnum;
  /**
   * id is used to automatically scroll to a specific item
   */
  id?: number;
  /**
   * grouping of program modules in player / acquise view
   */
  group?: string;
  iconDescription?: string;
  /**
   * additional ressources per item
   */
  additionalResources?: AdditionalResource[];
  actionCallback?: () => void;
  deleteCallback?: () => void;
  duplicateCallback?: () => void;
}

/**
 * Used to display tooltip messages on checkboxes, indicating the toggle action
 */
export interface DisplayTextProps {
  markAsCompleted: string;
  markAsUncompleted: string;
  additionalResourceText: string;
  readOn: string;
  showLess: string;
}

export const calculateIcon = (icon: TimelineIconEnum): any => {
  switch (icon) {
    case TimelineIconEnum.CHECKED:
      return <CheckIcon />;
    case TimelineIconEnum.UNCHECKED:
      return <LensIcon />;
    case TimelineIconEnum.STAR:
      return <StarIcon />;
    default:
      return <LensIcon style={{ color: theme.palette.primary.main }} />;
  }
};

export const VerticalTimeline: React.FC<VerticalTimelineProps> = ({
  items,
  scrollToId,
  mapKey,
  dotClickHandler,
  displayTexts = {
    markAsCompleted: 'Toggle',
    markAsUncompleted: 'Toggle',
    additionalResourceText: 'Weitere Ressourcen',
    readOn: 'Weiterlesen',
    showLess: 'Weniger anzeigen',
  },
}) => {
  const currentItemRef = useRef<HTMLDivElement>(null);
  const topOfTimelineRef = useRef<HTMLDivElement>(null);
  const [orderedGroupedItems, setOrderedGroupedItems] = useState(new Array());
  const [hoveredTimelineItem, setHoveredTimelineItem] = useState(null);

  useEffect(() => {
    if (scrollToId === -2) {
      return;
    }
    if (scrollToId === -1) {
      topOfTimelineRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    } else {
      currentItemRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }, [scrollToId]);

  // group items if their group property is set
  useEffect(() => {
    const map = new Map();
    items.forEach((item, index) => {
      if (!item.group || item.group === '') {
        map.set(index, item);
      } else {
        const groupItem = { ...item };
        delete groupItem.group;
        map.has(item.group)
          ? map.get(item.group).items.push(groupItem)
          : map.set(item.group, {
              group: item.group,
              items: [groupItem],
            });
      }
    });
    setOrderedGroupedItems(Array.from(map.values()));
  }, [items]);

  const handleDotClick = (itemId: number) => {
    if (dotClickHandler) {
      dotClickHandler(itemId);
    }
  };

  return (
    <Timeline sx={{ padding: 0 }}>
      <div ref={topOfTimelineRef}></div>
      {orderedGroupedItems.map((item, index) => {
        if (item.items && Array.isArray(item.items)) {
          return (
            <Paper
              key={'timeline-timeline-' + index}
              elevation={5}
              sx={{ margin: '2em 0', padding: '2em 0 4em 0' }}
            >
              <Box
                sx={{
                  marginLeft: '27.5%',
                  marginBottom: '2em',
                  maxWidth: '40em',
                  wordWrap: 'break-word',
                }}
              >
                <Typography variant="h5">{item.group}</Typography>
              </Box>
              <VerticalTimeline
                items={item.items}
                scrollToId={scrollToId}
                mapKey="grouped"
                dotClickHandler={dotClickHandler}
                displayTexts={displayTexts}
              ></VerticalTimeline>
            </Paper>
          );
        } else {
          return (
            <TimelineItem
              key={
                mapKey !== undefined
                  ? 'timeline-item' + index + mapKey
                  : 'timeline-item' + index
              }
              ref={
                scrollToId !== undefined && item.id === scrollToId
                  ? currentItemRef
                  : null
              }
              onMouseEnter={() => setHoveredTimelineItem(item.id)}
              onMouseLeave={() => setHoveredTimelineItem(null)}
            >
              <TimelineOppositeContent
                sx={{
                  margin: 'auto 0',
                  paddingRight: '1.5em',
                  flex: 0.2,
                }}
              >
                <Typography component="span" sx={{ whiteSpace: 'nowrap' }}>
                  {item.timelineText}
                </Typography>
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineConnector
                  sx={{
                    bgcolor:
                      index === 0 && mapKey !== 'grouped'
                        ? 'white'
                        : theme.palette.primary.main,
                  }}
                />
                <Tooltip
                  disableHoverListener={dotClickHandler === undefined}
                  arrow
                  placement="top"
                  title={
                    displayTexts
                      ? item.timelineIcon === TimelineIconEnum.UNCHECKED
                        ? displayTexts.markAsCompleted
                        : displayTexts.markAsUncompleted
                      : ''
                  }
                >
                  <TimelineDot
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      boxShadow:
                        hoveredTimelineItem === item.id
                          ? '0px 0px 4px black'
                          : 'none',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleDotClick(item.id)}
                  >
                    {item.timelineIcon ? (
                      calculateIcon(item.timelineIcon)
                    ) : (
                      <LensIcon style={{ color: theme.palette.primary.main }} />
                    )}
                  </TimelineDot>
                </Tooltip>
                <TimelineConnector
                  style={{
                    backgroundColor: theme.palette.primary.main,
                  }}
                />
              </TimelineSeparator>
              <TimelineContent
                sx={{
                  flex: 0.7,
                  margin: 'auto 0',
                  paddingBottom: '1em',
                  paddingTop: '1em',
                  paddingLeft: '1.5em',
                }}
              >
                <ObjectCardHorizontal
                  title={item.title ? item.title : ''}
                  description={item.description ? item.description : ''}
                  duration={item.duration ? item.duration : ''}
                  image={undefined}
                  imageSrc={
                    item.resolvedSrc === '' ? undefined : item.resolvedSrc
                  }
                  imageSize={item.imageSize}
                  cardSize={item.cardSize}
                  url={item.url}
                  icon={item.urlIcon}
                  actionCallback={item.actionCallback}
                  deleteCallback={item.deleteCallback}
                  duplicateCallback={item.duplicateCallback}
                  buttonDescription={item.iconDescription}
                  additionalResources={item.additionalResources}
                  displayTexts={displayTexts}
                />
              </TimelineContent>
            </TimelineItem>
          );
        }
      })}
    </Timeline>
  );
};
